/**
 * First-login setup by one-time token (D-037, D-132; routes `GET /auth/setup/:token`,
 * `POST /auth/setup/complete`): the user sets password, PIN and — when 2FA is required — confirms the
 * TOTP secret, then receives the recovery codes once. Reached at `/setup/<token>` (the link a manager
 * hands over after "add user" / "reset access").
 *
 * The old app had no equivalent screen (its first run asked for the shared super code only), so this
 * markup is new; it reuses the classes of the extracted Login card (premium-card, input and button
 * classes of packages/ui/src/screens/Login.tsx) so it reads as the same screen family. Candidate for
 * extraction into packages/ui in B4 (see README).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, KeyRound, ShieldCheck } from "lucide-react";
import { Logo, TotpQr, TotpSecretText, useI18n } from "@faylasuf/ui";
import { useApp } from "../app/context.js";
import { errorMessage } from "../app/errors.js";

const INPUT = "w-full bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] px-8 py-4 focus:outline-none focus:ring-8 focus:ring-primary-500/10 focus:border-primary-500 transition-all dark:text-[#f0f6fc] text-center text-xl font-black shadow-inner";
const LABEL = "block text-sm font-black text-gray-600 dark:text-[#8b949e] mb-3 uppercase tracking-[0.2em]";
const BUTTON = "w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-5 rounded-2xl font-black text-xl hover:from-primary-500 hover:to-primary-600 transition-all shadow-2xl shadow-primary-600/40 flex items-center justify-center gap-4 disabled:opacity-60";

export function SetupPage({ token, onDone }: { token: string; onDone: () => void }) {
  const { source, t } = useApp();
  const { locale } = useI18n();
  const info = useQuery({ queryKey: ["setup", token], queryFn: () => source.setupStart(token), retry: false, staleTime: Infinity });
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // [C37] F-370: the length rule of D-167 before sending (12 with the authenticator, 15 without), with its own message
    const minLength = info.data?.twoFactorRequired === false ? 15 : 12;
    if ([...password].length < minLength) return setError(t("setup.password_too_short", { min: minLength }));
    if (password !== password2) return setError(t("setup.password_mismatch"));
    if (!/^\d{6}$/.test(pin)) return setError(t("security.pin_format"));
    if (pin !== pin2) return setError(t("security.pin_mismatch"));
    setBusy(true);
    setError(null);
    try {
      const r = await source.setupComplete({ token, password, pin, ...(info.data?.totp ? { totpCode } : {}) });
      setRecoveryCodes(r.recoveryCodes);
    } catch (err) {
      setError(errorMessage(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex min-h-screen bg-[var(--bg-main)] items-center justify-center font-sans p-6 ${locale === "ar" ? "font-arabic" : ""}`} dir={locale === "ar" ? "rtl" : "ltr"} data-testid="setup-page">
      <div className="premium-card p-12 md:p-16 w-full max-w-2xl relative overflow-hidden">
        <div className="text-center mb-12 relative z-10">
          <div className="inline-block p-6 bg-primary-50 dark:bg-primary-900/20 rounded-[2.5rem] mb-8 shadow-inner border border-primary-100 dark:border-primary-500/10">
            <Logo size={80} className="mx-auto" />
          </div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-[#f0f6fc] tracking-tighter mb-3">{t("setup.title")}</h1>
          <p className="text-gray-500 dark:text-[#8b949e] font-black text-lg uppercase tracking-[0.2em]">{t("setup.subtitle")}</p>
        </div>

        {info.isError ? (
          <div className="space-y-8">
            <div className="text-center text-rose-600 dark:text-rose-400 font-black bg-rose-50 dark:bg-rose-900/20 py-4 rounded-2xl border border-rose-100 dark:border-rose-800/30">{t("setup.invalid_link")}</div>
            <button type="button" onClick={onDone} className={BUTTON}>{t("setup.to_login")}</button>
          </div>
        ) : recoveryCodes ? (
          <div className="space-y-8">
            <p className="text-center text-sm font-bold text-gray-500 dark:text-[#8b949e]">{t("setup.done")}</p>
            <div>
              <label className={LABEL}>{t("login.recovery_codes_title")}</label>
              <div data-testid="setup-recovery-codes" className="grid grid-cols-2 gap-2 p-6 bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] font-mono text-lg font-black text-gray-900 dark:text-[#f0f6fc] text-center" dir="ltr">
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mt-3 text-center">{t("login.recovery_codes_hint")}</p>
            </div>
            <button type="button" onClick={onDone} data-testid="setup-continue" className={BUTTON}>
              <span>{t("login.continue")}</span>
              <ArrowUpRight size={24} />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6 relative z-10">
            <div className="flex items-center gap-6 p-6 bg-primary-50/50 dark:bg-primary-900/10 rounded-[2.5rem] border border-primary-100 dark:border-primary-800/30">
              <div className="w-16 h-16 rounded-[1.5rem] bg-primary-600 flex items-center justify-center text-white shadow-2xl ring-8 ring-primary-500/10"><KeyRound size={32} /></div>
              <div>
                <p className="text-sm font-black text-primary-600 dark:text-primary-400 uppercase tracking-[0.2em]">{t("login.username")}</p>
                <h3 className="text-2xl font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-tighter">{info.data?.username ?? "…"}</h3>
                {info.data?.displayName && <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e]">{info.data.displayName}</p>}
              </div>
            </div>
            <div>
              <label className={LABEL}>{t("setup.password")}</label>
              <input type="password" required autoComplete="new-password" data-testid="setup-password" value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} />
              <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mt-2 text-center">{t("setup.password_hint")}</p>
            </div>
            <div>
              <label className={LABEL}>{t("setup.password_confirm")}</label>
              <input type="password" required autoComplete="new-password" data-testid="setup-password2" value={password2} onChange={(e) => setPassword2(e.target.value)} className={INPUT} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>{t("setup.pin")}</label>
                <input type="password" inputMode="numeric" maxLength={6} required autoComplete="off" data-testid="setup-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${INPUT} font-mono tracking-[0.4em]`} placeholder="••••••" />
              </div>
              <div>
                <label className={LABEL}>{t("setup.pin_confirm")}</label>
                <input type="password" inputMode="numeric" maxLength={6} required autoComplete="off" data-testid="setup-pin2" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${INPUT} font-mono tracking-[0.4em]`} placeholder="••••••" />
              </div>
            </div>
            {info.data?.totp && (
              <div className="space-y-4">
                <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] text-center">{t("login.enrol_qr")}</p>
                <TotpQr uri={info.data.totp.uri} label={t("login.enrol_qr")} testId="setup-qr" />
                <div>
                  <label className={LABEL}>{t("login.enrol_manual")} — {t("login.enrol_secret")}</label>
                  <TotpSecretText secret={info.data.totp.secret} testId="setup-secret" className="w-full bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] px-6 py-4 text-center font-mono text-lg font-black tracking-[0.15em] text-gray-900 dark:text-[#f0f6fc] shadow-inner select-all" />
                  <p className="text-[11px] font-bold text-gray-400 dark:text-[#8b949e] mt-2 text-center">{t("login.enrol_alphabet")}</p>
                </div>
                <div>
                  <label className={LABEL}>{t("login.second_factor_code")}</label>
                  <input type="text" inputMode="numeric" maxLength={6} required autoComplete="one-time-code" data-testid="setup-totp" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${INPUT} font-mono tracking-[0.4em]`} placeholder="••••••" />
                </div>
              </div>
            )}
            {error && (
              <div data-testid="setup-error" className="text-center text-rose-600 dark:text-rose-400 text-sm font-black bg-rose-50 dark:bg-rose-900/20 py-4 rounded-2xl border border-rose-100 dark:border-rose-800/30">{error}</div>
            )}
            <button type="submit" disabled={busy || !info.data} data-testid="setup-submit" className={BUTTON}>
              <ShieldCheck size={24} />
              <span>{t("setup.submit")}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
